import { HOME_PATH } from '../configs'
import { QCS_DEV_ENABLED } from './devFlags'

export const QCS_TAG = 'QCS'

/**
 * Account home (empty org list included) is driven by GET /getAdminUser
 * `isAuth0User`, not by bot count or `bot.isAuth0User`.
 */
export const isQcsAccountUser = adminUser => Boolean(adminUser?.isAuth0User)

/**
 * QCS segment on OrganisationScopeSwitch: Auth0 accounts always see it
 * (empty QCS home included); CareBrain accounts only when they have QCS orgs.
 */
export const shouldShowQcsScopeSegment = (adminUser, qcsCount) =>
  isQcsAccountUser(adminUser) || Number(qcsCount) > 0

/** Values for `GET /bot/list?platformFlag=` — CareBrain vs QCS organisation homes. */
export const PLATFORM_FLAG = {
  CAREBRAIN: 'carebrain',
  QCS: 'qcs',
}

export const QCS_BOT_IDS = []

export const QCS_DEV_OVERRIDE_KEY = 'qcsDevBotIds'

const DEV_SEED_IDS = String(process.env.REACT_APP_QCS_DEV_IDS || '')
  .split(',')
  .map(v => Number(v.trim()))
  .filter(id => id && !Number.isNaN(id))

let devOverrideRaw
let devOverrideIds = []

const readDevOverrideIds = () => {
  if (!QCS_DEV_ENABLED) return []

  let raw = null
  try {
    raw = window.localStorage.getItem(QCS_DEV_OVERRIDE_KEY)
  } catch {
    return DEV_SEED_IDS
  }

  if (raw === devOverrideRaw) return devOverrideIds

  devOverrideRaw = raw
  try {
    const parsed = JSON.parse(raw || '[]')
    const ids = Array.isArray(parsed) ? parsed.map(Number).filter(id => !Number.isNaN(id)) : []
    devOverrideIds = ids.length ? ids : DEV_SEED_IDS
  } catch {
    devOverrideIds = DEV_SEED_IDS
  }

  return devOverrideIds
}

/** Public read of the DEV localStorage override ids (Mark organisations as QCS). */
export const getQcsDevOverrideIds = () => readDevOverrideIds()

/**
 * True when this bot is QCS only because of the DEV localStorage override —
 * not because the API already flagged it. Used to merge/filter home lists while
 * `platformFlag` remains the server source of truth.
 */
export const isQcsDevOverrideOnly = bot => {
  if (!bot) return false
  if (bot.isQcsBot === true || bot.isQcs === true || bot.qcs === true) return false

  const source = String(bot.onboardingSource ?? bot.source ?? '').trim().toUpperCase()
  if (source === QCS_TAG) return false
  if (hasQcsTag(bot)) return false
  if (hasLegacyQcsFlag(bot)) return false

  const id = Number(bot.id)
  if (QCS_BOT_IDS.includes(id)) return false
  return readDevOverrideIds().includes(id)
}

export const countQcsDevOverrideOnly = bots =>
  (bots || []).filter(isQcsDevOverrideOnly).length

export const QCS_ALLOWED_TABS = ['dashboard', 'professionals', 'serviceUsers', 'settings']

export const QCS_ALLOWED_SETTINGS_ROUTES = [
  'users',
  'surveysSupervision',
  'requestLimits',
  'createSupervision/:templateId?',
]

export const QCS_ONLY_SETTINGS_ROUTES = ['requestLimits']

/** QCS Care Manager only — Admin / Careco admin still see these. */
export const QCS_MANAGER_HIDDEN_SETTINGS_ROUTES = ['requestLimits']

/** QCS Care Manager only — Admin / Careco Admin still see these sidebar tabs. */
export const QCS_MANAGER_HIDDEN_TABS = ['settings']

export const isQcsOnlySettingsRoute = path => QCS_ONLY_SETTINGS_ROUTES.includes(path)

export const isSettingsRouteHiddenForQcsManager = path =>
  QCS_MANAGER_HIDDEN_SETTINGS_ROUTES.includes(path)

export const isTabHiddenForQcsManager = path => QCS_MANAGER_HIDDEN_TABS.includes(path)

/**
 * First QCS sidebar tab this role actually sees (same order as `QCS_ALLOWED_TABS`
 * / Menu). Care Managers also land on Dashboard.
 * Role string matches `security.ROLE` — this file must not import security.
 *
 * For package / *FeatureUsed gates pass the bot into `resolveQcsLandingTab`
 * in `tabs/home/api/usageLimits` (org list / BotPlate). Callers without a bot
 * payload keep this role-only helper.
 */
export const getQcsLandingTab = role => {
  const hideForManager = role === 'ROLE_MANAGER'
  const tab = QCS_ALLOWED_TABS.find(path => !(hideForManager && isTabHiddenForQcsManager(path)))
  return tab || 'professionals'
}

const hasQcsTag = bot => {
  const tags = bot?.tags
  if (Array.isArray(tags)) {
    return tags.some(tag => String(tag?.name ?? tag).trim().toUpperCase() === QCS_TAG)
  }
  if (typeof tags === 'string') {
    return tags.split(',').some(tag => tag.trim().toUpperCase() === QCS_TAG)
  }
  return false
}

/** Legacy live-backend flag until backend ships canonical QCS tags. */
const hasLegacyQcsFlag = bot => Boolean(bot?.odinConfigs?.qcsEnabled)

/** Organisation type is only `isQcsBot`. Tags, source and odinConfigs.qcsEnabled are not it. */
export const isQcsBotPayload = bot => bot?.isQcsBot === true

/**
 * Looks a bot up by id from whatever the store already holds.
 *
 * Callers deep in the app often have only the id from the URL. Nothing is
 * fetched here — if the bot is not loaded yet the answer is "not QCS", which
 * errs towards showing more rather than hiding a tab that should be there. The
 * Menu waits for `activeBot` before rendering, so that window is not visible.
 */
export const findBotById = botId => {
  const id = Number(botId)
  if (Number.isNaN(id)) return null

  let state = null
  try {
    // eslint-disable-next-line global-require
    state = require('../index').store?.getState?.()
  } catch {
    return null
  }
  if (!state) return null

  if (Number(state.activeBot?.id) === id) return state.activeBot

  return (state.bots || []).find(bot => Number(bot.id) === id) || null
}

/**
 * True when this is a QCS organisation. Accepts a bot object or a bot id.
 *
 * Prefer passing the object — it is the honest answer. The id form exists for
 * call sites that only have the URL param.
 */
export const isQcsBot = botOrId => {
  if (botOrId && typeof botOrId === 'object') return isQcsBotPayload(botOrId)
  return isQcsBotPayload(findBotById(botOrId))
}

/**
 * Auth0 Careco Admin / Care Manager with exactly one organisation skip the
 * QCS list and open that bot. ROLE_ADMIN and multi-org accounts stay on the
 * list. Role strings match `security.ROLE` — this file must not import
 * security (circular: security already imports qcs).
 */
const QCS_SINGLE_ORG_SKIP_ROLES = ['ROLE_CARECO_ADMIN', 'ROLE_MANAGER']

export const shouldSkipQcsOrganisationsHome = adminUser => {
  if (!isQcsAccountUser(adminUser)) return false
  const accesses = adminUser.botAccesses
  if (!Array.isArray(accesses) || accesses.length !== 1) return false
  const access = accesses[0]
  if (access == null || access.botId == null || access.botId === '') return false
  return QCS_SINGLE_ORG_SKIP_ROLES.includes(access.role)
}

/**
 * `/admin/bot/{id}/{firstVisibleTab}` when the list should be skipped; otherwise null.
 * Optional `tabOverride` — pass `resolveQcsLandingTab(role, bot)` from home screens
 * so package / *FeatureUsed gates apply when the bot payload is already loaded.
 * Omitting it keeps the historic role-only landing (tests + callers without a bot).
 */
export const qcsAccountLandingPath = (adminUser, tabOverride) => {
  if (!shouldSkipQcsOrganisationsHome(adminUser)) return null
  const access = adminUser.botAccesses[0]
  const tab = tabOverride || getQcsLandingTab(access.role)
  return `${HOME_PATH}/bot/${access.botId}/${tab}`
}

export const isTabAllowedForQcs = path => QCS_ALLOWED_TABS.includes(path)

export const isSettingsRouteAllowedForQcs = path => QCS_ALLOWED_SETTINGS_ROUTES.includes(path)
